import React from 'react';
import { PageHelpButton } from './PageHelpButton';

export function PageHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 md:px-8 min-h-[64px] py-2 shrink-0 sticky top-0 z-10 flex-wrap"
      style={{ borderBottom: '1px solid var(--kmt-border)', background: 'rgba(10,10,11,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center kmt-panel-2 shrink-0" style={{ color: 'var(--kmt-gold)' }}>
            <Icon size={20} strokeWidth={1.9} />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="font-display text-base md:text-xl font-semibold leading-tight truncate">{title}</h1>
            <PageHelpButton />
          </div>
          {subtitle && <div className="kmt-label mt-0.5 truncate">{subtitle}</div>}
        </div>
      </div>
      {right && <div className="flex items-center gap-2 md:gap-3 flex-wrap">{right}</div>}
    </div>
  );
}

export function StatusBadge({ meta }) {
  return (
    <span className={`kmt-badge ${meta.cls}`}>
      <span className={`kmt-dot ${meta.dot || ''}`} />
      {meta.label}
    </span>
  );
}

export function StatCard({ label, value, accent, testId }) {
  return (
    <div className="kmt-panel p-5 flex flex-col justify-between" style={{ minHeight: 110 }} data-testid={testId}>
      <div className="kmt-label">{label}</div>
      <div className="font-display font-bold" style={{ fontSize: 40, lineHeight: 1, color: accent || 'var(--kmt-text)' }}>{value}</div>
    </div>
  );
}

export function Section({ title, children, right }) {
  return (
    <div className="kmt-panel overflow-hidden">
      {title && (
        <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid var(--kmt-border-soft)' }}>
          <div className="font-display font-semibold text-sm">{title}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)' }} onClick={onClose}>
      <div className="kmt-panel w-full max-w-lg kmt-fade-in flex flex-col my-4 sm:my-0" style={{ maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 shrink-0" style={{ borderBottom: '1px solid var(--kmt-border-soft)' }}>
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button onClick={onClose} style={{ color: 'var(--kmt-muted)' }} className="hover:text-white text-2xl leading-none px-1">×</button>
        </div>
        <div className="px-5 sm:px-6 py-5 overflow-y-auto">{children}</div>
        {footer && <div className="flex justify-end gap-2 px-5 sm:px-6 py-4 shrink-0" style={{ borderTop: '1px solid var(--kmt-border-soft)' }}>{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="kmt-label">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const HIST_ACTION_CLS = {
  CREATE: 's-completed', UPDATE: 's-progress', DELETE: 's-stopped',
  STATUS: 's-planned', SYNC: 's-break', REORDER: 's-hold',
};

export function HistoryModal({ open, onClose, entityType, entityId, api }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!open || !entityId) return;
    setLoading(true);
    api.listAudit(entityType, entityId).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [open, entityId, entityType, api]);

  const fmt = (ts) => { try { return new Date(ts).toLocaleString(); } catch (e) { return ts; } };

  return (
    <Modal open={open} onClose={onClose} title={`History · ${entityId || ''}`}
      footer={<button className="kmt-btn-ghost" onClick={onClose}>Close</button>}>
      {loading ? (
        <div className="py-8 text-center" style={{ color: 'var(--kmt-muted)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center" style={{ color: 'var(--kmt-muted)' }}>No change history for this item yet.</div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          {rows.map((e) => (
            <div key={e.id} className="flex items-start gap-3 pb-3" style={{ borderBottom: '1px solid var(--kmt-border-soft)' }}>
              <span className={`kmt-badge ${HIST_ACTION_CLS[e.action] || 's-planned'}`} style={{ minWidth: 72, justifyContent: 'center' }}>{e.action}</span>
              <div className="flex-1">
                <div className="text-sm">{e.detail}</div>
                <div className="kmt-label mt-1" style={{ fontSize: 9 }}>{fmt(e.ts)} · {e.actor}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
